import { useEventMixpanel } from '@mixpanel';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useState } from 'react';

import { Accordion, Box, Icon, InputBudget, InputText, Typography } from '@core/component';
import { Translate, util } from '@core/constant';
import { useContextCurrency } from '@core/context';
import { useDebounce, useLanguage } from '@core/hook';
import {
  EColor,
  EIcon,
  EMixpanelInteractionType,
  ESize,
  ETypographyVariant,
  Price,
  SelectChangeEvent,
} from '@core/type';
import { formatNumber, formatPrice, isStringWithNumber } from '@core/util';

import { BudgetCalculatorProps } from '../interface-product-show';
import { LinkRecommendation } from './LinkRecommendation';
import { StockMessage } from './StockMessage';

const LIMIT_PRICE = 15000;
const RECOMMENDED_COEFFICIENT = 0.1;

export const BudgetCalculator = ({
  product,
  currentStock,
  productPrice,
  logisticType,
  setProductCount,
  country,
  onAccordionAction,
}: BudgetCalculatorProps) => {
  const { language } = useLanguage();
  const { currency } = useContextCurrency();
  const { trackBudgetCalculatorEvent } = useEventMixpanel();

  const { t: tWidget } = useTranslation(Translate.common.WIDGET);

  const [inputPrice, setInputPrice] = useState<number>();
  const [inputQuantity, setInputQuantity] = useState<number>(0);
  const [hasLimitMessage, setHasLimitMessage] = useState<boolean>(false);
  const [hasStockMessage, setHasStockMessage] = useState<boolean>(false);
  const [hasRecommendation, setHasRecommendation] = useState<boolean>(false);
  const [recommendedToAdd, setRecommendedToAdd] = useState<number>(0);
  const [priceToShow, setPriceToShow] = useState<string>('');
  const [mixpanelEvent, setMixpanelEvent] = useState<EMixpanelInteractionType>(null);

  const priceDebounced = useDebounce<number>(inputPrice, util.DEBOUNCE_LONG);
  const quantityDebounced = useDebounce<number>(inputQuantity, util.DEBOUNCE_LONG);

  const handlePriceChange = ({ target: { value } }: SelectChangeEvent) => {
    if (isStringWithNumber(value)) {
      const price = parseFloat(value);
      const quantity = Math.floor(price / productPrice);

      updateRecommendation(EMixpanelInteractionType.BUDGET_ENTER, price, quantity, false, value);
    }
  };

  const handleQuantityChange = ({ target: { value } }: SelectChangeEvent) => {
    const quantity = parseInt(value);
    const price = quantity * productPrice;

    updateRecommendation(EMixpanelInteractionType.QUANTITY_ENTER, price, quantity, true);
  };

  const handleAddClick = () => {
    const price = inputPrice + recommendedToAdd;
    const quantity = Math.floor(price / productPrice);

    updateRecommendation(EMixpanelInteractionType.SUGGESTION_CLICK, price, quantity, true);
  };

  const updateRecommendation = (
    mixpanelEvent: EMixpanelInteractionType,
    price: number,
    quantity: number,
    hasFormattedPrice: boolean,
    inputPriceValue = '',
  ) => {
    const recommendedQuantity = Math.floor(
      (price + productPrice * RECOMMENDED_COEFFICIENT) / productPrice,
    );

    setInputPrice(isNaN(price) ? 0 : formatPrice(price));
    setInputQuantity(isNaN(quantity) ? 0 : quantity);
    setHasStockMessage(quantity > currentStock);
    setHasLimitMessage(price > LIMIT_PRICE);
    setRecommendedToAdd(productPrice - (price % productPrice));
    setHasRecommendation(recommendedQuantity > quantity);
    setProductCount(quantity > currentStock ? currentStock : !quantity ? 1 : quantity);

    if (hasFormattedPrice) {
      setPriceToShow(formatNumber(language, price, { minimumFractionDigits: 2 }));
    } else {
      setPriceToShow(inputPriceValue);
    }

    setMixpanelEvent(mixpanelEvent);

    if (mixpanelEvent && mixpanelEvent === EMixpanelInteractionType.SUGGESTION_CLICK) {
      sendMixpanelEvent(mixpanelEvent, price, quantity);
    }
  };

  const onFocus = () => {
    setPriceToShow(String(inputPrice));
  };

  const onBlur = () => {
    setPriceToShow(formatNumber(language, inputPrice, { minimumFractionDigits: 2 }));
  };

  const sendMixpanelEvent = (
    interactionType: EMixpanelInteractionType,
    budgetValue: Price,
    quantityValue: number,
  ) => {
    trackBudgetCalculatorEvent(
      product,
      interactionType,
      budgetValue,
      quantityValue,
      window.location.pathname,
      productPrice,
      currentStock,
    );
  };

  useEffect(() => {
    updateRecommendation(null, 0, 0, false);
  }, [logisticType, country, currency]);

  useEffect(() => {
    if (mixpanelEvent && mixpanelEvent === EMixpanelInteractionType.BUDGET_ENTER) {
      sendMixpanelEvent(mixpanelEvent, inputPrice, inputQuantity);
    }
  }, [priceDebounced]);

  useEffect(() => {
    if (mixpanelEvent && mixpanelEvent === EMixpanelInteractionType.QUANTITY_ENTER) {
      sendMixpanelEvent(mixpanelEvent, inputPrice, inputQuantity);
    }
  }, [quantityDebounced]);

  return (
    <Box
      marginBottom="24px"
      padding="16px 24px"
      borderRadius="10px"
      backgroundColor={{ semanticType: EColor.SECONDARY, variant: EColor.LIGHT }}
    >
      <Accordion
        titleContent={
          <Box display="flex" alignItems="center" gap="16px">
            <Icon type={EIcon.CALCULATOR} size={ESize.MD} />
            <Typography variant={ETypographyVariant.H6}>
              {tWidget('budgetCalculator.defineBudget')}
            </Typography>
          </Box>
        }
        onAnimationEnd={onAccordionAction}
      >
        <Box paddingTop="16px">
          {hasStockMessage && (
            <StockMessage hasLimitMessage={hasLimitMessage} currentStock={currentStock} />
          )}

          <Typography marginBottom="16px" variant={ETypographyVariant.BODY3}>
            {tWidget('budgetCalculator.findProducts')}
          </Typography>

          <Box display={'flex'} alignItems={'center'} gap={'3px'}>
            <InputBudget
              value={isNaN(inputPrice) || inputPrice === 0 ? '' : priceToShow}
              currency={currency}
              onChange={handlePriceChange}
              onFocus={onFocus}
              onBlur={onBlur}
              isSuccess={inputPrice > 0}
            />
            <Icon type={EIcon.TRANSFER} size={ESize.MD} />
            <Box width={'68px'} flexShrink={'0'}>
              <InputText
                name={'quantity'}
                fontSize={'14px'}
                value={String(inputQuantity)}
                textAlign={'center'}
                onChange={handleQuantityChange}
                isError={inputQuantity > currentStock}
                isSuccess={inputQuantity > 0}
              />
            </Box>
          </Box>
        </Box>

        {((hasRecommendation && !hasStockMessage && inputQuantity !== currentStock) ||
          (inputQuantity === 0 && inputPrice > 0)) && (
          <LinkRecommendation
            recommendedToAdd={recommendedToAdd}
            isOneProduct={inputQuantity === 0 && inputPrice > 0}
            onClick={handleAddClick}
          />
        )}
      </Accordion>
    </Box>
  );
};
